import { useEffect, useState, type FormEvent } from "react";
import type { Fuero, Materia, Organismo, Secretaria, Submateria } from "../../shared/types";
import { Boton } from "../components/Boton";
import { Campo, Input, Select } from "../components/Formulario";
import { Insignia } from "../components/Insignia";
import { Modal } from "../components/Modal";
import { Tabla, type ColumnaTabla } from "../components/Tabla";
import { useToast } from "../components/Toast";
import { api, ErrorApi } from "../lib/apiClient";

type Seccion = "materias" | "fueros" | "organismos";

const ETIQUETAS_SECCION: Record<Seccion, string> = {
  materias: "Materias",
  fueros: "Fueros",
  organismos: "Organismos y Secretarías",
};

// Administración de Materia/Submateria/Fuero (ver
// migrations/0006_taxonomia_juridica.sql) y Organismo/Secretaria (ver
// migrations/0007_organismos_secretarias.sql). Reordenar es simplemente
// editar el campo "orden" — no hace falta una interacción de arrastrar y
// soltar para este alcance.
export function SuperadminCatalogosPage() {
  const [seccion, setSeccion] = useState<Seccion>("materias");

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-white">
          Materias, Submaterias, Fueros, Organismos y Secretarías
        </h1>
        <p className="mt-1 text-sm text-slate-400">
          Catálogo jurídico usado por todos los estudios de la plataforma.
        </p>
      </div>

      <div className="flex gap-4 border-b border-slate-800">
        {(["materias", "fueros", "organismos"] as const).map((s) => (
          <button
            key={s}
            onClick={() => setSeccion(s)}
            className={`border-b-2 px-1 pb-2 text-sm font-medium transition-colors ${
              seccion === s
                ? "border-white text-white"
                : "border-transparent text-slate-400 hover:text-slate-200"
            }`}
          >
            {ETIQUETAS_SECCION[s]}
          </button>
        ))}
      </div>

      {seccion === "materias" && <PanelMaterias />}
      {seccion === "fueros" && <PanelFueros />}
      {seccion === "organismos" && <PanelOrganismos />}
    </div>
  );
}

function PanelMaterias() {
  const { mostrar } = useToast();
  const [materias, setMaterias] = useState<Materia[]>([]);
  const [cargando, setCargando] = useState(true);
  const [materiaExpandida, setMateriaExpandida] = useState<string | null>(null);
  const [submaterias, setSubmaterias] = useState<Submateria[]>([]);
  const [modalMateriaAbierto, setModalMateriaAbierto] = useState(false);
  const [materiaEnEdicion, setMateriaEnEdicion] = useState<Materia | null>(null);
  const [valoresMateria, setValoresMateria] = useState({ nombre: "", orden: "0" });
  const [guardandoMateria, setGuardandoMateria] = useState(false);
  const [modalSubmateriaAbierto, setModalSubmateriaAbierto] = useState(false);
  const [valoresSubmateria, setValoresSubmateria] = useState({ nombre: "", orden: "0" });
  const [guardandoSubmateria, setGuardandoSubmateria] = useState(false);

  async function cargarMaterias() {
    setCargando(true);
    try {
      const datos = await api.get<{ items: Materia[] }>("/superadmin/catalogos/materias");
      setMaterias(datos.items);
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudieron cargar las materias.", "error");
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => {
    cargarMaterias();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function verSubmaterias(materiaId: string) {
    if (materiaExpandida === materiaId) {
      setMateriaExpandida(null);
      return;
    }
    setMateriaExpandida(materiaId);
    try {
      const datos = await api.get<{ items: Submateria[] }>(
        `/superadmin/catalogos/materias/${materiaId}/submaterias`,
      );
      setSubmaterias(datos.items);
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudieron cargar las submaterias.", "error");
    }
  }

  function abrirAltaMateria() {
    setMateriaEnEdicion(null);
    setValoresMateria({ nombre: "", orden: "0" });
    setModalMateriaAbierto(true);
  }

  function abrirEdicionMateria(m: Materia) {
    setMateriaEnEdicion(m);
    setValoresMateria({ nombre: m.nombre, orden: String(m.orden) });
    setModalMateriaAbierto(true);
  }

  async function guardarMateria(e: FormEvent) {
    e.preventDefault();
    setGuardandoMateria(true);
    try {
      const cuerpo = { nombre: valoresMateria.nombre, orden: Number(valoresMateria.orden) };
      if (materiaEnEdicion) {
        await api.put(`/superadmin/catalogos/materias/${materiaEnEdicion.id}`, cuerpo);
        mostrar("Materia actualizada.", "exito");
      } else {
        await api.post("/superadmin/catalogos/materias", cuerpo);
        mostrar("Materia creada.", "exito");
      }
      setModalMateriaAbierto(false);
      await cargarMaterias();
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo guardar la materia.", "error");
    } finally {
      setGuardandoMateria(false);
    }
  }

  async function cambiarActivoMateria(m: Materia) {
    try {
      await api.patch(`/superadmin/catalogos/materias/${m.id}/activo`, { activo: !m.activo });
      mostrar(m.activo ? "Materia desactivada." : "Materia activada.", "exito");
      await cargarMaterias();
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo cambiar el estado.", "error");
    }
  }

  function abrirAltaSubmateria() {
    setValoresSubmateria({ nombre: "", orden: "0" });
    setModalSubmateriaAbierto(true);
  }

  async function guardarSubmateria(e: FormEvent) {
    e.preventDefault();
    if (!materiaExpandida) return;
    setGuardandoSubmateria(true);
    try {
      await api.post(`/superadmin/catalogos/materias/${materiaExpandida}/submaterias`, {
        nombre: valoresSubmateria.nombre,
        orden: Number(valoresSubmateria.orden),
      });
      mostrar("Submateria creada.", "exito");
      setModalSubmateriaAbierto(false);
      await verSubmateriasForzado(materiaExpandida);
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo crear la submateria.", "error");
    } finally {
      setGuardandoSubmateria(false);
    }
  }

  async function verSubmateriasForzado(materiaId: string) {
    const datos = await api.get<{ items: Submateria[] }>(
      `/superadmin/catalogos/materias/${materiaId}/submaterias`,
    );
    setSubmaterias(datos.items);
  }

  async function cambiarActivoSubmateria(s: Submateria) {
    try {
      await api.patch(`/superadmin/catalogos/submaterias/${s.id}/activo`, { activo: !s.activo });
      mostrar(s.activo ? "Submateria desactivada." : "Submateria activada.", "exito");
      if (materiaExpandida) await verSubmateriasForzado(materiaExpandida);
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo cambiar el estado.", "error");
    }
  }

  const columnas: ColumnaTabla<Materia>[] = [
    { encabezado: "Orden", render: (m) => m.orden },
    { encabezado: "Materia", render: (m) => m.nombre },
    {
      encabezado: "Estado",
      render: (m) => <Insignia texto={m.activo ? "Activo" : "Inactivo"} />,
    },
    {
      encabezado: "Acciones",
      render: (m) => (
        <div className="flex gap-3">
          <button
            onClick={() => verSubmaterias(m.id)}
            className="font-medium text-slate-600 hover:text-slate-900"
          >
            {materiaExpandida === m.id ? "Ocultar submaterias" : "Ver submaterias"}
          </button>
          <button
            onClick={() => abrirEdicionMateria(m)}
            className="font-medium text-slate-600 hover:text-slate-900"
          >
            Editar
          </button>
          <button
            onClick={() => cambiarActivoMateria(m)}
            className={
              m.activo
                ? "font-medium text-red-600 hover:text-red-700"
                : "font-medium text-emerald-600 hover:text-emerald-700"
            }
          >
            {m.activo ? "Desactivar" : "Activar"}
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Boton onClick={abrirAltaMateria}>+ Nueva Materia</Boton>
      </div>

      <Tabla
        columnas={columnas}
        filas={materias}
        claveFila={(m) => m.id}
        cargando={cargando}
        tituloVacio="Sin materias"
        descripcionVacio="Creá la primera con el botón de arriba."
      />

      {materiaExpandida && (
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-900">
              Submaterias de {materias.find((m) => m.id === materiaExpandida)?.nombre}
            </h3>
            <Boton variante="secundario" onClick={abrirAltaSubmateria}>
              + Nueva Submateria
            </Boton>
          </div>
          <ul className="divide-y divide-slate-100">
            {submaterias.map((s) => (
              <li key={s.id} className="flex items-center justify-between py-2 text-sm">
                <span className="text-slate-700">
                  {s.orden}. {s.nombre}
                </span>
                <div className="flex items-center gap-3">
                  <Insignia texto={s.activo ? "Activo" : "Inactivo"} />
                  <button
                    onClick={() => cambiarActivoSubmateria(s)}
                    className={
                      s.activo
                        ? "font-medium text-red-600 hover:text-red-700"
                        : "font-medium text-emerald-600 hover:text-emerald-700"
                    }
                  >
                    {s.activo ? "Desactivar" : "Activar"}
                  </button>
                </div>
              </li>
            ))}
            {submaterias.length === 0 && (
              <li className="py-2 text-sm text-slate-400">Sin submaterias todavía.</li>
            )}
          </ul>
        </div>
      )}

      <Modal
        titulo={materiaEnEdicion ? "Editar materia" : "Nueva materia"}
        abierto={modalMateriaAbierto}
        onCerrar={() => setModalMateriaAbierto(false)}
      >
        <form onSubmit={guardarMateria} className="space-y-4">
          <Campo etiqueta="Nombre" obligatorio>
            <Input
              required
              value={valoresMateria.nombre}
              onChange={(e) => setValoresMateria((v) => ({ ...v, nombre: e.target.value }))}
            />
          </Campo>
          <Campo etiqueta="Orden">
            <Input
              type="number"
              value={valoresMateria.orden}
              onChange={(e) => setValoresMateria((v) => ({ ...v, orden: e.target.value }))}
            />
          </Campo>
          <div className="flex justify-end gap-2">
            <Boton type="button" variante="secundario" onClick={() => setModalMateriaAbierto(false)}>
              Cancelar
            </Boton>
            <Boton type="submit" cargando={guardandoMateria}>
              Guardar
            </Boton>
          </div>
        </form>
      </Modal>

      <Modal
        titulo="Nueva submateria"
        abierto={modalSubmateriaAbierto}
        onCerrar={() => setModalSubmateriaAbierto(false)}
      >
        <form onSubmit={guardarSubmateria} className="space-y-4">
          <Campo etiqueta="Nombre" obligatorio>
            <Input
              required
              value={valoresSubmateria.nombre}
              onChange={(e) => setValoresSubmateria((v) => ({ ...v, nombre: e.target.value }))}
            />
          </Campo>
          <Campo etiqueta="Orden">
            <Input
              type="number"
              value={valoresSubmateria.orden}
              onChange={(e) => setValoresSubmateria((v) => ({ ...v, orden: e.target.value }))}
            />
          </Campo>
          <div className="flex justify-end gap-2">
            <Boton type="button" variante="secundario" onClick={() => setModalSubmateriaAbierto(false)}>
              Cancelar
            </Boton>
            <Boton type="submit" cargando={guardandoSubmateria}>
              Guardar
            </Boton>
          </div>
        </form>
      </Modal>
    </div>
  );
}

function PanelFueros() {
  const { mostrar } = useToast();
  const [fueros, setFueros] = useState<Fuero[]>([]);
  const [cargando, setCargando] = useState(true);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [fueroEnEdicion, setFueroEnEdicion] = useState<Fuero | null>(null);
  const [valores, setValores] = useState({ nombre: "", orden: "0" });
  const [guardando, setGuardando] = useState(false);

  async function cargar() {
    setCargando(true);
    try {
      const datos = await api.get<{ items: Fuero[] }>("/superadmin/catalogos/fueros");
      setFueros(datos.items);
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudieron cargar los fueros.", "error");
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function abrirAlta() {
    setFueroEnEdicion(null);
    setValores({ nombre: "", orden: "0" });
    setModalAbierto(true);
  }

  function abrirEdicion(f: Fuero) {
    setFueroEnEdicion(f);
    setValores({ nombre: f.nombre, orden: String(f.orden) });
    setModalAbierto(true);
  }

  async function guardar(e: FormEvent) {
    e.preventDefault();
    setGuardando(true);
    try {
      const cuerpo = { nombre: valores.nombre, orden: Number(valores.orden) };
      if (fueroEnEdicion) {
        await api.put(`/superadmin/catalogos/fueros/${fueroEnEdicion.id}`, cuerpo);
        mostrar("Fuero actualizado.", "exito");
      } else {
        await api.post("/superadmin/catalogos/fueros", cuerpo);
        mostrar("Fuero creado.", "exito");
      }
      setModalAbierto(false);
      await cargar();
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo guardar el fuero.", "error");
    } finally {
      setGuardando(false);
    }
  }

  async function cambiarActivo(f: Fuero) {
    try {
      await api.patch(`/superadmin/catalogos/fueros/${f.id}/activo`, { activo: !f.activo });
      mostrar(f.activo ? "Fuero desactivado." : "Fuero activado.", "exito");
      await cargar();
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo cambiar el estado.", "error");
    }
  }

  const columnas: ColumnaTabla<Fuero>[] = [
    { encabezado: "Orden", render: (f) => f.orden },
    { encabezado: "Fuero", render: (f) => f.nombre },
    {
      encabezado: "Estado",
      render: (f) => <Insignia texto={f.activo ? "Activo" : "Inactivo"} />,
    },
    {
      encabezado: "Acciones",
      render: (f) => (
        <div className="flex gap-3">
          <button
            onClick={() => abrirEdicion(f)}
            className="font-medium text-slate-600 hover:text-slate-900"
          >
            Editar
          </button>
          <button
            onClick={() => cambiarActivo(f)}
            className={
              f.activo
                ? "font-medium text-red-600 hover:text-red-700"
                : "font-medium text-emerald-600 hover:text-emerald-700"
            }
          >
            {f.activo ? "Desactivar" : "Activar"}
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Boton onClick={abrirAlta}>+ Nuevo Fuero</Boton>
      </div>

      <Tabla
        columnas={columnas}
        filas={fueros}
        claveFila={(f) => f.id}
        cargando={cargando}
        tituloVacio="Sin fueros"
        descripcionVacio="Creá el primero con el botón de arriba."
      />

      <Modal
        titulo={fueroEnEdicion ? "Editar fuero" : "Nuevo fuero"}
        abierto={modalAbierto}
        onCerrar={() => setModalAbierto(false)}
      >
        <form onSubmit={guardar} className="space-y-4">
          <Campo etiqueta="Nombre" obligatorio>
            <Input
              required
              value={valores.nombre}
              onChange={(e) => setValores((v) => ({ ...v, nombre: e.target.value }))}
            />
          </Campo>
          <Campo etiqueta="Orden">
            <Input
              type="number"
              value={valores.orden}
              onChange={(e) => setValores((v) => ({ ...v, orden: e.target.value }))}
            />
          </Campo>
          <div className="flex justify-end gap-2">
            <Boton type="button" variante="secundario" onClick={() => setModalAbierto(false)}>
              Cancelar
            </Boton>
            <Boton type="submit" cargando={guardando}>
              Guardar
            </Boton>
          </div>
        </form>
      </Modal>
    </div>
  );
}

// Organismo pertenece a un Fuero (por eso el selector de Fuero arriba de
// todo, igual que Submateria depende de Materia); Secretaría pertenece a
// un Organismo y se administra anidada acá mismo (mismo patrón "ver
// hijos" ya usado en Materias/Submaterias) en vez de una pestaña aparte,
// para no obligar a un doble selector en cascada (Fuero→Organismo) antes
// de ver ninguna Secretaría.
function PanelOrganismos() {
  const { mostrar } = useToast();
  const [fueros, setFueros] = useState<Fuero[]>([]);
  const [fueroId, setFueroId] = useState("");
  const [organismos, setOrganismos] = useState<Organismo[]>([]);
  const [cargando, setCargando] = useState(true);
  const [organismoExpandido, setOrganismoExpandido] = useState<string | null>(null);
  const [secretarias, setSecretarias] = useState<Secretaria[]>([]);
  const [modalOrganismoAbierto, setModalOrganismoAbierto] = useState(false);
  const [organismoEnEdicion, setOrganismoEnEdicion] = useState<Organismo | null>(null);
  const [valoresOrganismo, setValoresOrganismo] = useState({ nombre: "", orden: "0" });
  const [guardandoOrganismo, setGuardandoOrganismo] = useState(false);
  const [modalSecretariaAbierto, setModalSecretariaAbierto] = useState(false);
  const [valoresSecretaria, setValoresSecretaria] = useState({ nombre: "", orden: "0" });
  const [guardandoSecretaria, setGuardandoSecretaria] = useState(false);

  useEffect(() => {
    api.get<{ items: Fuero[] }>("/superadmin/catalogos/fueros").then((datos) => {
      setFueros(datos.items);
      if (datos.items.length > 0) setFueroId(datos.items[0].id);
    });
  }, []);

  async function cargarOrganismos(id: string) {
    if (!id) return;
    setCargando(true);
    try {
      const datos = await api.get<{ items: Organismo[] }>(
        `/superadmin/catalogos/fueros/${id}/organismos`,
      );
      setOrganismos(datos.items);
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudieron cargar los organismos.", "error");
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => {
    setOrganismoExpandido(null);
    cargarOrganismos(fueroId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fueroId]);

  async function verSecretarias(organismoId: string) {
    if (organismoExpandido === organismoId) {
      setOrganismoExpandido(null);
      return;
    }
    setOrganismoExpandido(organismoId);
    try {
      const datos = await api.get<{ items: Secretaria[] }>(
        `/superadmin/catalogos/organismos/${organismoId}/secretarias`,
      );
      setSecretarias(datos.items);
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudieron cargar las secretarías.", "error");
    }
  }

  function abrirAltaOrganismo() {
    setOrganismoEnEdicion(null);
    setValoresOrganismo({ nombre: "", orden: "0" });
    setModalOrganismoAbierto(true);
  }

  function abrirEdicionOrganismo(o: Organismo) {
    setOrganismoEnEdicion(o);
    setValoresOrganismo({ nombre: o.nombre, orden: String(o.orden) });
    setModalOrganismoAbierto(true);
  }

  async function guardarOrganismo(e: FormEvent) {
    e.preventDefault();
    setGuardandoOrganismo(true);
    try {
      const cuerpo = { nombre: valoresOrganismo.nombre, orden: Number(valoresOrganismo.orden) };
      if (organismoEnEdicion) {
        await api.put(`/superadmin/catalogos/organismos/${organismoEnEdicion.id}`, cuerpo);
        mostrar("Organismo actualizado.", "exito");
      } else {
        await api.post(`/superadmin/catalogos/fueros/${fueroId}/organismos`, cuerpo);
        mostrar("Organismo creado.", "exito");
      }
      setModalOrganismoAbierto(false);
      await cargarOrganismos(fueroId);
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo guardar el organismo.", "error");
    } finally {
      setGuardandoOrganismo(false);
    }
  }

  async function cambiarActivoOrganismo(o: Organismo) {
    try {
      await api.patch(`/superadmin/catalogos/organismos/${o.id}/activo`, { activo: !o.activo });
      mostrar(o.activo ? "Organismo desactivado." : "Organismo activado.", "exito");
      await cargarOrganismos(fueroId);
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo cambiar el estado.", "error");
    }
  }

  function abrirAltaSecretaria() {
    setValoresSecretaria({ nombre: "", orden: "0" });
    setModalSecretariaAbierto(true);
  }

  async function verSecretariasForzado(organismoId: string) {
    const datos = await api.get<{ items: Secretaria[] }>(
      `/superadmin/catalogos/organismos/${organismoId}/secretarias`,
    );
    setSecretarias(datos.items);
  }

  async function guardarSecretaria(e: FormEvent) {
    e.preventDefault();
    if (!organismoExpandido) return;
    setGuardandoSecretaria(true);
    try {
      await api.post(`/superadmin/catalogos/organismos/${organismoExpandido}/secretarias`, {
        nombre: valoresSecretaria.nombre,
        orden: Number(valoresSecretaria.orden),
      });
      mostrar("Secretaría creada.", "exito");
      setModalSecretariaAbierto(false);
      await verSecretariasForzado(organismoExpandido);
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo crear la secretaría.", "error");
    } finally {
      setGuardandoSecretaria(false);
    }
  }

  async function cambiarActivoSecretaria(s: Secretaria) {
    try {
      await api.patch(`/superadmin/catalogos/secretarias/${s.id}/activo`, { activo: !s.activo });
      mostrar(s.activo ? "Secretaría desactivada." : "Secretaría activada.", "exito");
      if (organismoExpandido) await verSecretariasForzado(organismoExpandido);
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo cambiar el estado.", "error");
    }
  }

  const columnas: ColumnaTabla<Organismo>[] = [
    { encabezado: "Orden", render: (o) => o.orden },
    { encabezado: "Organismo", render: (o) => o.nombre },
    {
      encabezado: "Estado",
      render: (o) => <Insignia texto={o.activo ? "Activo" : "Inactivo"} />,
    },
    {
      encabezado: "Acciones",
      render: (o) => (
        <div className="flex gap-3">
          <button
            onClick={() => verSecretarias(o.id)}
            className="font-medium text-slate-600 hover:text-slate-900"
          >
            {organismoExpandido === o.id ? "Ocultar secretarías" : "Ver secretarías"}
          </button>
          <button
            onClick={() => abrirEdicionOrganismo(o)}
            className="font-medium text-slate-600 hover:text-slate-900"
          >
            Editar
          </button>
          <button
            onClick={() => cambiarActivoOrganismo(o)}
            className={
              o.activo
                ? "font-medium text-red-600 hover:text-red-700"
                : "font-medium text-emerald-600 hover:text-emerald-700"
            }
          >
            {o.activo ? "Desactivar" : "Activar"}
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Campo etiqueta="Fuero">
          <Select value={fueroId} onChange={(e) => setFueroId(e.target.value)} className="w-64">
            {fueros.map((f) => (
              <option key={f.id} value={f.id}>
                {f.nombre}
              </option>
            ))}
          </Select>
        </Campo>
        <Boton onClick={abrirAltaOrganismo} disabled={!fueroId}>
          + Nuevo Organismo
        </Boton>
      </div>

      <Tabla
        columnas={columnas}
        filas={organismos}
        claveFila={(o) => o.id}
        cargando={cargando}
        tituloVacio="Sin organismos"
        descripcionVacio="Creá el primero con el botón de arriba."
      />

      {organismoExpandido && (
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-900">
              Secretarías de {organismos.find((o) => o.id === organismoExpandido)?.nombre}
            </h3>
            <Boton variante="secundario" onClick={abrirAltaSecretaria}>
              + Nueva Secretaría
            </Boton>
          </div>
          <ul className="divide-y divide-slate-100">
            {secretarias.map((s) => (
              <li key={s.id} className="flex items-center justify-between py-2 text-sm">
                <span className="text-slate-700">
                  {s.orden}. {s.nombre}
                </span>
                <div className="flex items-center gap-3">
                  <Insignia texto={s.activo ? "Activo" : "Inactivo"} />
                  <button
                    onClick={() => cambiarActivoSecretaria(s)}
                    className={
                      s.activo
                        ? "font-medium text-red-600 hover:text-red-700"
                        : "font-medium text-emerald-600 hover:text-emerald-700"
                    }
                  >
                    {s.activo ? "Desactivar" : "Activar"}
                  </button>
                </div>
              </li>
            ))}
            {secretarias.length === 0 && (
              <li className="py-2 text-sm text-slate-400">Sin secretarías todavía.</li>
            )}
          </ul>
        </div>
      )}

      <Modal
        titulo={organismoEnEdicion ? "Editar organismo" : "Nuevo organismo"}
        abierto={modalOrganismoAbierto}
        onCerrar={() => setModalOrganismoAbierto(false)}
      >
        <form onSubmit={guardarOrganismo} className="space-y-4">
          <Campo etiqueta="Nombre" obligatorio>
            <Input
              required
              value={valoresOrganismo.nombre}
              onChange={(e) => setValoresOrganismo((v) => ({ ...v, nombre: e.target.value }))}
            />
          </Campo>
          <Campo etiqueta="Orden">
            <Input
              type="number"
              value={valoresOrganismo.orden}
              onChange={(e) => setValoresOrganismo((v) => ({ ...v, orden: e.target.value }))}
            />
          </Campo>
          <div className="flex justify-end gap-2">
            <Boton type="button" variante="secundario" onClick={() => setModalOrganismoAbierto(false)}>
              Cancelar
            </Boton>
            <Boton type="submit" cargando={guardandoOrganismo}>
              Guardar
            </Boton>
          </div>
        </form>
      </Modal>

      <Modal
        titulo="Nueva secretaría"
        abierto={modalSecretariaAbierto}
        onCerrar={() => setModalSecretariaAbierto(false)}
      >
        <form onSubmit={guardarSecretaria} className="space-y-4">
          <Campo etiqueta="Nombre" obligatorio>
            <Input
              required
              value={valoresSecretaria.nombre}
              onChange={(e) => setValoresSecretaria((v) => ({ ...v, nombre: e.target.value }))}
            />
          </Campo>
          <Campo etiqueta="Orden">
            <Input
              type="number"
              value={valoresSecretaria.orden}
              onChange={(e) => setValoresSecretaria((v) => ({ ...v, orden: e.target.value }))}
            />
          </Campo>
          <div className="flex justify-end gap-2">
            <Boton type="button" variante="secundario" onClick={() => setModalSecretariaAbierto(false)}>
              Cancelar
            </Boton>
            <Boton type="submit" cargando={guardandoSecretaria}>
              Guardar
            </Boton>
          </div>
        </form>
      </Modal>
    </div>
  );
}
